import { CloseTwoTone } from '@mui/icons-material';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Toolbar,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ISalesOrderInput } from '../utils/types';
import { HtmlContent } from './HtmlContent';
import { useAuth } from '../hooks/useAuth';

export function ContractDialog(props: {
  open: boolean;
  onClose: (accepted?: boolean) => void;
  tourId: number;
  data: ISalesOrderInput[];
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const { token } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);

  const fetchContent = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(
        `/api/online/tours/${props.tourId}/sales-contract`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(props.data),
        },
      );
      if (!res.ok) {
        setError('Failed to fetch contract');
        return;
      }
      const data = await res.text();
      setContent(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [props.tourId, props.data, token]);

  useEffect(() => {
    if (props.open) {
      fetchContent();
    }
  }, [fetchContent, props.open]);

  return (
    <Dialog open={props.open} onClose={() => props.onClose()} fullScreen>
      <AppBar sx={{ position: 'relative' }} elevation={0}>
        <Toolbar>
          {t('contractDialog.title')}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            autoFocus
            color="inherit"
            onClick={() => props.onClose(true)}
            sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}
          >
            {t('contractDialog.accept')}
          </Button>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => props.onClose()}
            aria-label="close"
          >
            <CloseTwoTone />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        <HtmlContent content={content || ''} />
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          color="inherit"
          onClick={() => {
            props.onClose();
          }}
        >
          {t('contractDialog.close')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            props.onClose(true);
          }}
        >
          {t('contractDialog.accept')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
